import { useEffect } from 'react';

/**
 * Injects a single `<script type="application/ld+json">` BreadcrumbList
 * schema into <head> for the lifetime of the mounting component, then
 * removes it on unmount. SEO-safe: Google reads JSON-LD that is
 * dynamically injected at runtime, so this works for the SPA pages
 * without adding react-helmet.
 *
 * Usage:
 *   <BreadcrumbSchema items={[
 *     { name: 'Home', url: 'https://printmindai.com/' },
 *     { name: 'Free Demo', url: 'https://printmindai.com/preview' },
 *   ]} />
 */
const BreadcrumbSchema = ({ items }) => {
  useEffect(() => {
    if (!Array.isArray(items) || items.length === 0) return;

    const payload = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((it, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: it.name,
        item: it.url,
      })),
    };

    const tag = document.createElement('script');
    tag.type = 'application/ld+json';
    // `data-pm-breadcrumb` lets us spot it in DevTools and avoids any
    // collision with the static JSON-LD blocks already in index.html.
    tag.setAttribute('data-pm-breadcrumb', '1');
    tag.textContent = JSON.stringify(payload);
    document.head.appendChild(tag);

    return () => {
      // Clean up so SPA navigations don't accumulate breadcrumb tags.
      document.head.removeChild(tag);
    };
  }, [items]);

  return null;
};

export default BreadcrumbSchema;
